import {generateGitHubFileMetadata} from '@github-ui/copilot-chat-helpers/custom-copilots-helpers'
import type {CopilotChatRepo} from '@github-ui/copilot-chat-types'
import type {CopilotSpaceFreeTextResource, CopilotSpaceResource, ResourceCounts} from '../../types'
import {fetchRepo} from '../../utils/fetch-repo'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {testIdProps} from '@github-ui/test-id-props'
import {AlertIcon, EyeIcon, KebabHorizontalIcon, PencilIcon, TrashIcon} from '@primer/octicons-react'
import {
  ActionList,
  ActionMenu,
  Button,
  Checkbox,
  Dialog,
  IconButton,
  Label,
  Link,
  Stack,
  Truncate,
  VisuallyHidden,
} from '@primer/react'
import {type Column, DataTable, Table} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {useCallback, useMemo, useRef, useState} from 'react'

import {AttachmentIcon} from './AttachmentIcon'
import {AttachmentPreview} from './AttachmentPreview'
import styles from './ReferencesTable.module.css'
import {SpacesFilePicker} from '../SpacesFilePicker'
import {TextFileDialog} from './TextFileDialog'
import {uniqueBy} from './utils/unique-by'

interface ReferencesTableProps {
  resources: CopilotSpaceResource[]
  resourceCounts?: ResourceCounts
  onDeleteResources: (resources: CopilotSpaceResource[]) => void
  onUpdateResource: (resource: CopilotSpaceResource) => void
  onResourcesAdded: (resources: CopilotSpaceResource[]) => void
  sizePercentage?: number
  owner?: string
  readOnly?: boolean
}

export function ReferencesTable({
  resources,
  resourceCounts,
  onDeleteResources,
  onUpdateResource,
  sizePercentage,
  onResourcesAdded,
  owner,
  readOnly = false,
}: ReferencesTableProps) {
  const uniqueResources = useMemo(() => uniqueBy(resources, getUniqueKey), [resources])
  const [isDialogOpen, setDialogOpen] = useState(false)
  const [copilotSpaceResource, setCopilotSpaceResource] = useState<CopilotSpaceResource | null>(null)
  const returnFocusRef = useRef<HTMLButtonElement>(null)

  const [selectedResources, setSelectedResources] = useState<CopilotSpaceResource[]>([])

  const handleSelectResource = useCallback(
    (row: CopilotSpaceResource) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setSelectedResources(prev =>
        e.target.checked ? [...prev, row] : prev.filter(resource => resource.id !== row.id),
      )
    },
    [],
  )

  const handleDeleteSelectedResources = useCallback(() => {
    if (selectedResources.length > 0) {
      onDeleteResources(selectedResources)
      setSelectedResources([])
    }
  }, [onDeleteResources, selectedResources])

  const handleBatchSelectResources = useCallback(() => {
    if (selectedResources.length > 0) {
      setSelectedResources([])
    } else {
      setSelectedResources(uniqueResources)
    }
  }, [selectedResources, uniqueResources])

  const columns = useMemo(() => {
    const checkBoxColumn: Column<CopilotSpaceResource> = {
      id: 'select',
      header: () => (
        <Checkbox
          checked={selectedResources.length === uniqueResources.length}
          indeterminate={selectedResources.length > 0 && selectedResources.length < uniqueResources.length}
          aria-label="Select all sources"
          value="default"
          onChange={handleBatchSelectResources}
        />
      ),
      width: 'auto',
      renderCell: row => {
        return (
          <Checkbox
            checked={selectedResources.some(resource => resource.id === row.id)}
            aria-label="Select source"
            value={row.id}
            onChange={handleSelectResource(row)}
          />
        )
      },
    }
    const fileColumn: Column<CopilotSpaceResource> = {
      id: 'id',
      header: () => (
        <span className={styles.headerText}>
          {selectedResources.length > 0
            ? `${selectedResources.length} selected`
            : `${uniqueResources.length} ${uniqueResources.length === 1 ? 'source' : 'sources'}`}
        </span>
      ),
      rowHeader: true,
      renderCell: row => {
        switch (row.type) {
          case 'github_file': {
            const {filePath, fileName, fileUrl, sha} = generateGitHubFileMetadata(row)

            return (
              <Stack direction="horizontal" align="center" gap="normal">
                <AttachmentIcon attachment={row} className="fgColor-muted" />
                <Stack gap="none">
                  <div>
                    {row.fileExists ? (
                      <Stack direction="horizontal" wrap="wrap">
                        <Truncate title={fileName} className={styles.referenceTitle}>
                          <Link rel="noopener" target="_blank" href={fileUrl} className={styles.referenceTitleLink}>
                            {fileName}
                          </Link>
                        </Truncate>
                        {sha && <Label className="ml-1">{sha.substring(0, 7)}</Label>}
                      </Stack>
                    ) : (
                      <Truncate title={fileName} className={styles.referenceTitle}>
                        {fileName}
                      </Truncate>
                    )}
                  </div>
                  <div>
                    <Truncate title={filePath} className={clsx(styles.referenceSubtitle, 'fgColor-muted')}>
                      {filePath}
                    </Truncate>
                  </div>
                </Stack>
              </Stack>
            )
          }
          case 'free_text':
            return (
              <Stack direction="horizontal" align="center" gap="normal">
                <AttachmentIcon attachment={row} className="fgColor-muted" />
                <div>
                  <Truncate title={row.name} maxWidth={300} className={styles.referenceTitle}>
                    {row.name}
                  </Truncate>
                </div>
              </Stack>
            )
          case 'repository':
            return (
              <Stack direction="horizontal" align="center" gap="normal">
                <GitHubAvatar square={row.ownerType === 'Organization'} src={row.ownerAvatarUrl} />
                <Stack gap="none">
                  <div>
                    <Truncate title={row.nwo} maxWidth={300} className={styles.referenceTitle}>
                      <Link
                        className={styles.referenceTitleLink}
                        href={`/${row.nwo}`}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        {row.nwo}
                      </Link>
                    </Truncate>
                  </div>
                  {!readOnly && (
                    <div className={styles.repositoryWarning} {...testIdProps('repo-warning')}>
                      Spaces work best with specific files added.
                    </div>
                  )}
                </Stack>
              </Stack>
            )
          case 'uploaded_text_file':
            return (
              <Stack direction="horizontal" align="center" gap="normal">
                <AttachmentIcon attachment={row} className="fgColor-muted" />
                <div>
                  <Truncate title={row.name} maxWidth={300} className={styles.referenceTitle}>
                    <Link
                      className={styles.referenceTitleLink}
                      href={row.url}
                      // The download attribute doesn't work cross-domain, but we include it for intent and future same-domain use.
                      // Downloads work because Copilot::ChatAttachment serves files with Content-Disposition: attachment header.
                      download={row.name}
                    >
                      {row.name}
                    </Link>
                  </Truncate>
                </div>
              </Stack>
            )
          case 'github_pull_request':
          case 'github_issue':
            return (
              <Stack direction="horizontal" align="center" gap="normal">
                <span>
                  <AttachmentIcon attachment={row} className="fgColor-success" />
                </span>
                <Stack gap="none">
                  <div>
                    <Truncate title={row.title || ''} className={styles.referenceTitle}>
                      <Link rel="noopener" target="_blank" href={row.url} className={styles.referenceTitleLink}>
                        {row.title} <span className="fgColor-muted text-light">{`#${row.number}`}</span>
                      </Link>
                    </Truncate>
                  </div>
                  <div>
                    <Truncate title={row.nwo || ''} className={clsx(styles.referenceSubtitle, 'fgColor-muted')}>
                      {row.nwo}
                    </Truncate>
                  </div>
                </Stack>
              </Stack>
            )
          case 'media_content':
            return (
              <Stack direction="horizontal" align="center" gap="normal">
                <span className="fgColor-muted">
                  <AttachmentIcon attachment={row} className="fgColor-muted" />
                </span>
                <Link
                  as="button"
                  className={styles.referenceTitleLink}
                  ref={returnFocusRef}
                  onClick={e => {
                    e.preventDefault()
                    setCopilotSpaceResource(row)
                    setDialogOpen(true)
                  }}
                  aria-haspopup="dialog"
                  aria-expanded={isDialogOpen}
                >
                  <Truncate title={row.name} maxWidth={300} className={clsx(styles.referenceTitle)}>
                    {row.name}
                  </Truncate>
                </Link>
              </Stack>
            )
          default:
            // log error Unsupported resource type
            return null
        }
      },
    }
    const sizeColumn: Column<CopilotSpaceResource> = {
      id: 'size',
      header: () => <VisuallyHidden>Size</VisuallyHidden>,
      align: 'end',
      maxWidth: 'auto',
      width: 'growCollapse',
      renderCell: row => {
        return <RowSize row={row} onResourcesAdded={onResourcesAdded} owner={owner} resourceCounts={resourceCounts} />
      },
    }
    const actionsColumn: Column<CopilotSpaceResource> = {
      id: 'actions',
      header: () => (
        <>
          <VisuallyHidden>Actions</VisuallyHidden>
          <Button
            style={{visibility: selectedResources.length > 0 && !readOnly ? 'visible' : 'hidden'}}
            size="small"
            aria-label="Remove selected resources"
            onClick={handleDeleteSelectedResources}
          >
            Remove
          </Button>
        </>
      ),
      maxWidth: 'auto',
      align: 'end',
      renderCell: row => {
        const shouldShowWarning = row.type === 'github_file' && !row.fileExists
        return (
          <>
            {shouldShowWarning && (
              <IconButton
                className={styles.warningIcon}
                icon={AlertIcon}
                variant="invisible"
                size="small"
                aria-label="This file no longer exists"
              />
            )}
            <RowActions
              row={row}
              onEdit={onUpdateResource}
              onDelete={resource => onDeleteResources([resource])}
              sizePercentage={sizePercentage}
              readOnly={readOnly}
            />
          </>
        )
      },
    }

    const columnsProps: Array<Column<CopilotSpaceResource>> = readOnly
      ? [fileColumn, sizeColumn, actionsColumn]
      : [checkBoxColumn, fileColumn, sizeColumn, actionsColumn]

    return columnsProps
  }, [
    selectedResources,
    uniqueResources.length,
    handleBatchSelectResources,
    handleSelectResource,
    isDialogOpen,
    handleDeleteSelectedResources,
    onUpdateResource,
    sizePercentage,
    onResourcesAdded,
    owner,
    onDeleteResources,
    readOnly,
    resourceCounts,
  ])

  const onCloseDialog = () => {
    setCopilotSpaceResource(null)
    setDialogOpen(false)
  }

  return (
    <>
      <Table.Container>
        <DataTable aria-labelledby="references-title" cellPadding="normal" data={uniqueResources} columns={columns} />
      </Table.Container>
      {isDialogOpen && copilotSpaceResource && copilotSpaceResource.type === 'media_content' && (
        <Dialog
          onClose={onCloseDialog}
          position={{narrow: 'fullscreen'}}
          returnFocusRef={returnFocusRef}
          title={copilotSpaceResource.name}
        >
          <AttachmentPreview attachment={copilotSpaceResource} />
        </Dialog>
      )}
    </>
  )
}

interface RowActionsProps {
  row: CopilotSpaceResource
  onEdit: (updatedResource: CopilotSpaceResource) => void
  onDelete: (row: CopilotSpaceResource) => void
  sizePercentage?: number
  readOnly?: boolean
}

interface RowSizeProps {
  row: CopilotSpaceResource
  onResourcesAdded: (resources: CopilotSpaceResource[]) => void
  owner?: string
  resourceCounts?: ResourceCounts
}

const RowSize = ({row, onResourcesAdded, owner, resourceCounts}: RowSizeProps) => {
  // For opening the repo file picker when editing a repository resource
  const [showMultiFilePicker, setShowMultiFilePicker] = useState(false)
  const [repoForPicker, setRepoForPicker] = useState<CopilotChatRepo | null>(null)
  const [isLoadingRepo, setIsLoadingRepo] = useState(false)
  const editRepoButtonRef = useRef<HTMLButtonElement>(null)

  const fetchAndOpenRepoPicker = async (repoId: number) => {
    setIsLoadingRepo(true)

    try {
      const repoDetails = await fetchRepo(repoId)

      setRepoForPicker(repoDetails)
      setShowMultiFilePicker(true)
    } finally {
      setIsLoadingRepo(false)
    }
  }

  if (row.type === 'repository') {
    return (
      <>
        <IconButton
          variant="invisible"
          className="float-left"
          ref={editRepoButtonRef}
          loading={isLoadingRepo}
          aria-label="Add specific files"
          onClick={async () => {
            await fetchAndOpenRepoPicker(row.repositoryId)
          }}
          icon={PencilIcon}
        />
        {showMultiFilePicker && repoForPicker && (
          <SpacesFilePicker
            resourceCounts={resourceCounts}
            isOpen={showMultiFilePicker}
            onClose={() => setShowMultiFilePicker(false)}
            onResourcesAdded={onResourcesAdded}
            owner={owner}
            initialRepo={repoForPicker}
            hideBackButton
          />
        )}
      </>
    )
  } else if ('sizePercentage' in row) {
    return (
      <span className="fgColor-muted">
        {row.sizePercentage && row.sizePercentage < 1 ? '<1%' : `${Math.round(row.sizePercentage || 0)}%`}
      </span>
    )
  }
}

const RowActions = ({row, onDelete, onEdit, sizePercentage, readOnly = false}: RowActionsProps) => {
  const [showEditResourceDialog, setShowEditResourceDialog] = useState<CopilotSpaceFreeTextResource | null>(null)

  let attachmentName = ''
  switch (row.type) {
    case 'github_pull_request':
    case 'github_issue':
      attachmentName = row.title
      break
    case 'github_file':
      attachmentName = row.filePath
      break
    case 'repository':
      attachmentName = row.nwo
      break
    case 'free_text':
    case 'uploaded_text_file':
    case 'media_content':
      attachmentName = row.name
      break
  }

  return (
    <>
      <div className="d-md-none">
        {(!readOnly || (readOnly && row.type === 'free_text')) && (
          <ActionMenu>
            <ActionMenu.Anchor>
              <IconButton
                aria-label={`Attachment actions: ${attachmentName}`}
                title={`Attachment actions`}
                icon={KebabHorizontalIcon}
                variant="invisible"
              />
            </ActionMenu.Anchor>
            <ActionMenu.Overlay align="end">
              <ActionList>
                {row.type === 'free_text' && (
                  <>
                    <ActionList.Item
                      aria-label={`${readOnly ? 'View' : 'Edit'} attachment: ${row.id}`}
                      onSelect={() => setShowEditResourceDialog(row)}
                    >
                      <ActionList.LeadingVisual>{readOnly ? <EyeIcon /> : <PencilIcon />}</ActionList.LeadingVisual>
                      <span>{readOnly ? 'View' : 'Edit'}</span>
                    </ActionList.Item>
                  </>
                )}
                {!readOnly && (
                  <ActionList.Item
                    aria-label={`Delete attachment: ${row.id}`}
                    variant="danger"
                    onSelect={() => onDelete(row)}
                  >
                    <ActionList.LeadingVisual>
                      <TrashIcon />
                    </ActionList.LeadingVisual>
                    Delete
                  </ActionList.Item>
                )}
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        )}
      </div>
      <div className="d-none d-md-flex flex-md-row">
        {row.type === 'free_text' && (
          <IconButton
            onClick={() => setShowEditResourceDialog(row)}
            variant="invisible"
            aria-label={readOnly ? 'View' : 'Edit'}
            icon={readOnly ? EyeIcon : PencilIcon}
          />
        )}
        {!readOnly && (
          <IconButton onClick={() => onDelete(row)} variant="invisible" aria-label="Delete" icon={TrashIcon} />
        )}
      </div>

      {showEditResourceDialog && (
        <TextFileDialog
          sizePercentage={sizePercentage}
          resource={showEditResourceDialog}
          onSave={onEdit}
          onClose={() => setShowEditResourceDialog(null)}
          readOnly={readOnly}
        />
      )}
    </>
  )
}

const getUniqueKey = (resource: CopilotSpaceResource): string => {
  switch (resource.type) {
    case 'github_file':
      return `${resource.repositoryId}@${resource.filePath}@${resource.sha}`
    case 'github_issue':
    case 'github_pull_request':
      return resource.url
    default:
      return String(resource.id)
  }
}
